/**
 * Centralized equipment slot configuration
 * Single source of truth for all slot metadata used across UI components
 *
 * To add a new slot:
 * 1. Add entry here in SLOT_CONFIG
 * 2. Add equipment items in equipmentData.ts
 * 3. Add to EquippedGear interface in types/equipment.ts
 * 4. Add defaults in defaults/defaultPlayerState.ts
 */

import { EquipmentSlot, EquippedGear } from '@/types';
import { EQUIPMENT_DATA } from './equipmentData';

export interface SlotMetadata {
  icon: string;
  label: string;
  pluralLabel: string;
  silhouettePosition: 'left' | 'right' | 'top' | 'bottom-left' | 'bottom-right';
  sortOrder: number;
  enabled: boolean;
  /** Conditional visibility: slot only active when dependency is met */
  dependsOn?: { slot: EquipmentSlot; tackleTypes: string[] };
}

export const SLOT_CONFIG: Record<EquipmentSlot, SlotMetadata> = {
  [EquipmentSlot.ROD]:    { icon: '🎣', label: 'Rod',    pluralLabel: 'Rods',    silhouettePosition: 'left',         sortOrder: 1, enabled: true },
  [EquipmentSlot.REEL]:   { icon: '⚙️', label: 'Reel',   pluralLabel: 'Reels',   silhouettePosition: 'right',        sortOrder: 2, enabled: true },
  [EquipmentSlot.TACKLE]: { icon: '🎯', label: 'Tackle', pluralLabel: 'Tackle',  silhouettePosition: 'bottom-right', sortOrder: 3, enabled: true },
  [EquipmentSlot.BAIT]:   { icon: '🪱', label: 'Bait',   pluralLabel: 'Bait',    silhouettePosition: 'top',          sortOrder: 4, enabled: true, dependsOn: { slot: EquipmentSlot.TACKLE, tackleTypes: ['bobber', 'weight'] } },
  [EquipmentSlot.LINE]:   { icon: '🧵', label: 'Line',   pluralLabel: 'Lines',   silhouettePosition: 'bottom-left',  sortOrder: 5, enabled: true },
  [EquipmentSlot.TRAP]:   { icon: '🪤', label: 'Trap',   pluralLabel: 'Traps',   silhouettePosition: 'bottom-right', sortOrder: 6, enabled: true },
  [EquipmentSlot.BOAT]:   { icon: '⛵', label: 'Boat',   pluralLabel: 'Boats',   silhouettePosition: 'bottom-left',  sortOrder: 7, enabled: false },
};

/** Check whether the BAIT slot is currently active based on equipped tackle or fly rod */
export function isBaitSlotActive(equippedGear: EquippedGear): boolean {
  // Fly rods always enable the bait slot (for fly patterns)
  const rodId = equippedGear[EquipmentSlot.ROD];
  if (rodId) {
    const rodDef = EQUIPMENT_DATA[rodId];
    if (rodDef?.isFlyRod) return true;
  }

  // Conventional: bait requires bobber or weight tackle
  const tackleId = equippedGear[EquipmentSlot.TACKLE];
  if (!tackleId) return false;
  const tackleDef = EQUIPMENT_DATA[tackleId];
  if (!tackleDef) return false;
  return tackleDef.tackleType === 'bobber' || tackleDef.tackleType === 'weight';
}

/** Check if the currently equipped rod is a fly rod */
export function isEquippedRodFlyRod(equippedGear: EquippedGear): boolean {
  const rodId = equippedGear[EquipmentSlot.ROD];
  if (!rodId) return false;
  const rodDef = EQUIPMENT_DATA[rodId];
  return rodDef?.isFlyRod === true;
}

/** Get only currently-enabled slot types, sorted by sortOrder. Pass equippedGear to filter conditional slots. */
export function getActiveSlots(equippedGear?: EquippedGear): EquipmentSlot[] {
  return (Object.entries(SLOT_CONFIG) as [EquipmentSlot, SlotMetadata][])
    .filter(([, meta]) => {
      if (!meta.enabled) return false;
      if (meta.dependsOn && equippedGear) {
        const depItemId = equippedGear[meta.dependsOn.slot];
        if (!depItemId) return false;
        const depDef = EQUIPMENT_DATA[depItemId];
        if (!depDef?.tackleType) return false;
        return meta.dependsOn.tackleTypes.includes(depDef.tackleType);
      }
      return true;
    })
    .sort((a, b) => a[1].sortOrder - b[1].sortOrder)
    .map(([slot]) => slot);
}

/** Build slot filter options for shop UIs */
export function getSlotFilters(equippedGear?: EquippedGear): { id: 'all' | EquipmentSlot; label: string }[] {
  return [
    { id: 'all' as const, label: 'All' },
    ...getActiveSlots(equippedGear).map((slot) => ({
      id: slot,
      label: SLOT_CONFIG[slot].pluralLabel,
    })),
  ];
}
